<template>
  <modal-wrap :name="modalName" :header-class="'user-header'" body-class="user-modal" :hide-footer="true" size="lg">
    <template #modal-title>
      <div class="d-flex justify-content-between align-items-center">
        <h4 class="mb-0">{{ title }}</h4>
      </div>
    </template>
    <template #default>
      <VeeForm @submit="handleSubmit">
        <div class="mt-2">
          <b-alert v-if="isDefaultMissing" :model-value="true" variant="danger" class="mb-3">
            Default company is not configured. Please set a default company before creating staff users.
          </b-alert>
          <b-alert v-if="mode === 'create'" :model-value="true" variant="info" class="mb-3">
            The user will receive a password setup link at the provided email address.
          </b-alert>
          <div class="form-group">
            <Field
              v-slot="{ field, errors, meta }"
              :rules="{ required: true, email: true }"
              :model-value="form.email"
              name="email"
            >
              <label for="email">Email *</label>
              <input
                v-bind="field"
                v-model="form.email"
                type="email"
                :class="['form-control', getValidationState(meta)]"
              />
              <span class="text-danger">{{ errors.join() }}</span>
            </Field>
          </div>

          <div class="row">
            <div class="col-12 col-md-6">
              <div class="form-group">
                <Field
                  v-slot="{ field, errors, meta }"
                  :model-value="form.firstName"
                  name="firstName"
                  :rules="nameRules"
                >
                  <label for="firstName">First name *</label>
                  <input
                    v-bind="field"
                    v-model="form.firstName"
                    type="text"
                    :class="['form-control', getValidationState(meta)]"
                  />
                  <span class="text-danger">{{ errors.join() }}</span>
                </Field>
              </div>
            </div>
            <div class="col-12 col-md-6">
              <div class="form-group">
                <Field
                  v-slot="{ field, errors, meta }"
                  :model-value="form.lastName"
                  name="lastName"
                  :rules="nameRules"
                >
                  <label for="lastName">Last name *</label>
                  <input
                    v-bind="field"
                    v-model="form.lastName"
                    type="text"
                    :class="['form-control', getValidationState(meta)]"
                  />
                  <span class="text-danger">{{ errors.join() }}</span>
                </Field>
              </div>
            </div>
          </div>

          <div v-if="mode === 'edit'" class="row">
            <div class="col-12 col-md-6">
              <div class="form-group">
                <Field
                  v-slot="{ field, errors, meta }"
                  :model-value="password"
                  :rules="{ password: true, required: false }"
                  name="password"
                >
                  <label for="password">Password</label>
                  <input
                    v-bind="field"
                    v-model="password"
                    type="password"
                    autocomplete="off"
                    placeholder="Password"
                    :class="['form-control', getValidationState(meta)]"
                  />
                  <span class="text-danger">{{ errors.join() }}</span>
                </Field>
              </div>
            </div>
            <div class="col-12 col-md-6">
              <div class="form-group">
                <Field
                  v-slot="{ field, errors, meta }"
                  :model-value="confirmedPassword"
                  :rules="{ confirmed: '@password', password: true, required: false }"
                  name="confirmedPassword"
                >
                  <label for="confirmedPassword">Confirm Password</label>
                  <input
                    v-bind="field"
                    v-model="confirmedPassword"
                    type="password"
                    autocomplete="off"
                    placeholder="Confirm Password"
                    :class="['form-control', getValidationState(meta)]"
                  />
                  <span class="text-danger">{{ errors.join() }}</span>
                </Field>
              </div>
            </div>
          </div>
          <div v-if="mode === 'edit'" class="text-muted mb-2">
            New password is set only if provided and both fields match.
          </div>

          <div class="form-check mb-3">
            <input
              id="isInventoryAvailable"
              v-model="form.isInventoryAvailable"
              type="checkbox"
              class="form-check-input"
            />
            <label class="form-check-label" for="isInventoryAvailable">Inventory Management access</label>
          </div>

          <div class="sender-footer mt-3">
            <div class="w-100 m-0">
              <b-button type="submit" variant="success" class="w-100" :disabled="submitting || isDefaultMissing">
                {{ actionLabel }}
              </b-button>
            </div>
          </div>
        </div>
      </VeeForm>
    </template>
  </modal-wrap>
</template>

<script lang="ts">
import type { StaffUser } from '@/types/Models/StaffUser'
import { BAlert } from 'bootstrap-vue-next'
import { Form as VeeForm, Field } from 'vee-validate'
import { computed, defineComponent, onMounted, ref, watch, type ComputedRef } from 'vue'
import { getValidationState } from '@/services/Validation'
import { useCustomersStore } from '@/store/customers'
import { useStaffUsersStore } from '@/store/staffUsers'
import { useVueNextModalStore } from '@/store/vueNextModal'
import ModalWrap from '@/views/Modals/ModalWrap.vue'

export default defineComponent({
  name: 'StaffUserModal',
  components: {
    BAlert,
    ModalWrap,
    VeeForm,
    Field,
  },
  props: {
    modalName: {
      type: String,
      required: true,
    },
    mode: {
      type: String as () => 'create' | 'edit',
      required: true,
    },
  },
  emits: ['saved'],
  setup(props, { emit }) {
    const modalStore = useVueNextModalStore()
    const staffUsersStore = useStaffUsersStore()
    const customersStore = useCustomersStore()
    const submitting = ref(false)
    const form = ref({
      id: '',
      firstName: '',
      lastName: '',
      email: '',
      isInventoryAvailable: true,
    })
    const password = ref('')
    const confirmedPassword = ref('')
    const nameRules = { required: true, min: 2 }
    const defaultCompanyId = computed(() => customersStore.allCustomers?.find((company) => company.isDefault)?.id || null)
    const isDefaultMissing = computed(() => props.mode === 'create' && !defaultCompanyId.value)
    const modalData: ComputedRef<{ user?: StaffUser }> = computed(() => modalStore.modalPropsByName(props.modalName) || {})
    const title = computed(() => (props.mode === 'create' ? 'Create staff user' : 'Edit staff user'))
    const actionLabel = computed(() => (props.mode === 'create' ? 'Create' : 'Save'))

    const closeModal = () => {
      modalStore.closeModal(props.modalName)
    }

    const resetForm = () => {
      form.value = {
        id: '',
        firstName: '',
        lastName: '',
        email: '',
        isInventoryAvailable: true,
      }
      password.value = ''
      confirmedPassword.value = ''
    }

    const setFormFromUser = (user?: StaffUser) => {
      form.value = {
        id: user?.id || '',
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        email: user?.email || '',
        isInventoryAvailable: user?.isInventoryAvailable ?? true,
      }
      password.value = ''
      confirmedPassword.value = ''
    }

    const handleSubmit = () => {
      submitting.value = true
      if (props.mode === 'create') {
        if (!defaultCompanyId.value) {
          submitting.value = false
          return
        }
        const payload: {
          type: string
          companyId: string
          firstName: string
          lastName: string
          email: string
          isInventoryAvailable: boolean
        } = {
          type: 'staff',
          companyId: defaultCompanyId.value,
          firstName: form.value.firstName,
          lastName: form.value.lastName,
          email: form.value.email,
          isInventoryAvailable: form.value.isInventoryAvailable,
        }

        return staffUsersStore
          .createStaffUser(payload)
          .then((created) => {
            if (created !== false) {
              resetForm()
              closeModal()
              emit('saved')
            }
          })
          .finally(() => {
            submitting.value = false
          })
      }

      if (!form.value.id) {
        submitting.value = false
        return
      }

      return staffUsersStore
        .updateStaffUser({
          id: form.value.id,
          data: {
            firstName: form.value.firstName,
            lastName: form.value.lastName,
            email: form.value.email,
            isInventoryAvailable: form.value.isInventoryAvailable,
            ...(password.value ? { password: password.value } : {}),
          },
        })
        .then((saved) => {
          if (saved !== false) {
            closeModal()
            emit('saved')
          }
        })
        .finally(() => {
          submitting.value = false
        })
    }

    onMounted(() => {
      if (props.mode === 'edit') {
        setFormFromUser(modalData.value?.user)
      }
    })

    watch(
      () => modalData.value.user,
      (user) => {
        if (props.mode === 'edit') {
          setFormFromUser(user)
        }
      },
    )

    return {
      title,
      actionLabel,
      isDefaultMissing,
      form,
      password,
      confirmedPassword,
      nameRules,
      submitting,
      handleSubmit,
      getValidationState,
    }
  },
})
</script>
